import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { buildApiUrl, getAuthHeaders } from '../../utils/apiBase';

export default function SettingsAmazon() {
    const [integration, setIntegration] = useState<any>({});
    const [loading, setLoading] = useState(false);

    const refreshData = async () => {
        setLoading(true);
        try {
            const res = await fetch(buildApiUrl('/settings/amazon-settings'), {
                headers: getAuthHeaders({
                    'Content-Type': 'application/json',
                }),
            });
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setIntegration(data.data || {});
        } catch (err) {
            console.error('Failed to fetch amazon settings:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        refreshData();
    }, []);

    const [formData, setFormData] = useState({
        active: false,
        client_id: '',
        client_secret: '',
        refresh_token: '',
        marketplace_id: '',
        seller_id: '',
        sandbox_mode: true,
    });

    useEffect(() => {
        if (integration) {
            setFormData({
                active: integration.active || false,
                client_id: integration.client_id || '',
                client_secret: integration.client_secret || '',
                refresh_token: integration.refresh_token || '',
                marketplace_id: integration.marketplace_id || '',
                seller_id: integration.seller_id || '',
                sandbox_mode: integration.sandbox_mode ?? true,
            });
        }
    }, [integration]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        const checked = (e.target as HTMLInputElement).checked;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        try {
            const res = await fetch(buildApiUrl('/settings/amazon-settings/update'), {
                method: 'POST',
                headers: getAuthHeaders({
                    'Content-Type': 'application/json',
                }),
                body: JSON.stringify(formData),
            });
            if (!res.ok) throw new Error('Failed to update');
            Swal.fire({
                title: 'Success',
                text: 'Amazon settings updated successfully!',
                icon: 'success'
            });
            refreshData();
        } catch (err) {
            console.error('Failed to save amazon settings:', err);
            Swal.fire({
                title: 'Error',
                text: 'Failed to save amazon settings',
                icon: 'error'
            });
        }
    };

    return (
        <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 overflow-hidden">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-800 dark:text-white">Amazon Marketplace Settings</h3>
                <div className="flex items-center gap-2">
                    {loading && <span className="text-sm text-gray-500">Loading...</span>}
                </div>
            </div>
            <div className="p-6">
                <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            name="active"
                            id="active"
                            checked={formData.active}
                            onChange={handleInputChange}
                            className="w-4 h-4 rounded text-brand-500"
                        />
                        <label htmlFor="active" className="text-sm font-medium">Active</label>
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            name="sandbox_mode"
                            id="sandbox_mode"
                            checked={formData.sandbox_mode}
                            onChange={handleInputChange}
                            className="w-4 h-4 rounded text-brand-500"
                        />
                        <label htmlFor="sandbox_mode" className="text-sm font-medium">Sandbox Mode</label>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Client ID</label>
                            <input
                                type="text"
                                name="client_id"
                                value={formData.client_id}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Client Secret</label>
                            <input
                                type="password"
                                name="client_secret"
                                value={formData.client_secret}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium mb-1">Refresh Token</label>
                        <input
                            type="password"
                            name="refresh_token"
                            value={formData.refresh_token}
                            onChange={handleInputChange}
                            className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium mb-1">Marketplace ID</label>
                            <input
                                type="text"
                                name="marketplace_id"
                                value={formData.marketplace_id}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium mb-1">Seller ID</label>
                            <input
                                type="text"
                                name="seller_id"
                                value={formData.seller_id}
                                onChange={handleInputChange}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-gray-800 dark:border-gray-700"
                            />
                        </div>
                    </div>
                    <div className="flex justify-end gap-3 mt-6">
                        <button
                            type="submit"
                            className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600"
                        >
                            Save Settings
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
